import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import axios from "axios";
import { ToastContainer } from "react-toastify";

import LandingPageBuilder from "./LandingPageBuilder";
import { errorText, toastError } from "../../../shared/toastHelper";

const LandingPageContext = createContext(null);
export const useLandingPage = () => useContext(LandingPageContext);

const LandingPageBuilderContainer = (props) => {
  const [page, setPage] = useState(props.landingPage);
  const [venueData, setVenueData] = useState({ events: [], segments: [], tags: [] });
  const [previewView, setPreviewView] = useState("page");
  const [lastErrors, setLastErrors] = useState([]);
  const saves = useRef(Promise.resolve(true));
  const pending = useRef(0);
  const confirmed = useRef(props.landingPage);
  const basePath = `/teams/${props.team.id}/pages/${props.landingPage.id}`;
  axios.defaults.headers.common["X-CSRF-TOKEN"] = props.csrfToken;

  const enqueue = (request, fallback) => {
    pending.current += 1;
    setLastErrors([]);
    saves.current = saves.current
      .then(request)
      .then(({ data }) => {
        confirmed.current = data;
        pending.current -= 1;
        if (pending.current === 0) setPage(data);
        return true;
      })
      .catch((error) => {
        pending.current -= 1;
        if (pending.current === 0) setPage(confirmed.current);
        setLastErrors(Array.isArray(error.response?.data) ? error.response.data : []);
        toastError(errorText(error, fallback));
        return false;
      });
    return saves.current;
  };

  const savePage = useCallback((changes) => {
    setPage((prev) => ({
      ...prev,
      ...changes,
      settings: { ...prev.settings, ...(changes.settings || {}) },
    }));
    return enqueue(() => axios.put(basePath, { landing_page: changes }), "Couldn't save your changes.");
  }, [basePath]);

  const loadVenueData = useCallback((venueOwnershipId) => {
    if (!venueOwnershipId) return;
    axios.get(`${basePath}/venue_data`, { params: { venue_ownership_id: venueOwnershipId } })
      .then(({ data }) => setVenueData(data))
      .catch((error) => toastError(errorText(error, "Couldn't load this venue's events and segments.")));
  }, [basePath]);

  const searchEvents = useCallback((venueOwnershipId, q) =>
    axios.get(`${basePath}/events`, { params: { venue_ownership_id: venueOwnershipId, q } })
      .then(({ data }) => data)
      .catch((error) => {
        toastError(errorText(error, "Couldn't search this venue's events."));
        return [];
      }), [basePath]);

  const transition = (action, fallback) => enqueue(() => axios.post(`${basePath}/${action}`), fallback);

  const value = {
    page,
    props,
    venueData,
    loadVenueData,
    searchEvents,
    savePage,
    publish: () => transition("publish", "Couldn't publish this page."),
    unpublish: () => transition("unpublish", "Couldn't unpublish this page."),
    waitForSaves: () => saves.current,
    previewView,
    setPreviewView,
    lastErrors,
  };

  return (
    <LandingPageContext.Provider value={value}>
      <ToastContainer icon={false} />
      <LandingPageBuilder />
    </LandingPageContext.Provider>
  );
};

export default LandingPageBuilderContainer;
